import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import { checkPermission } from "@/core/helpers/permissions";

function throwId(route) {
  return { id: +route.params.id };
}

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "dashboard",
    component: () => import("@/components/Dashboard"),
    redirect: { name: "cameras", params: { page: 1 } },
    children: [
      {
        path: "/cameras/create",
        name: "cameras:create",
        component: () => import("@/components/Cameras/components/Camera/CameraCreate"),
        meta: {
          permission: "cameras:create",
        },
      },
      {
        path: "/cameras/edit/:id",
        name: "cameras:edit",
        component: () => import("@/components/Cameras/components/Camera/Camera"),
        props: throwId,
        meta: {
          permission: "cameras:read",
        },
      },
      {
        path: "/cameras/:page",
        name: "cameras",
        component: () => import("@/components/Cameras/components/CamerasList"),
        meta: {
          permission: "cameras:read",
        },
      },
      {
        path: "/dashboard",
        name: "cameras:monitoring",
        component: () => import("@/components/Cameras/components/CamerasMonitoring"),
        meta: {
          permission: "cameras_state:read",
        },
      },
      {
        path: "/servers/create",
        name: "servers:create",
        component: () => import("@/components/Server/ServerCreate"),
        meta: {
          permission: "dvr_servers:create",
        },
      },
      {
        path: "/servers/edit/:id",
        name: "servers:edit",
        props: throwId,
        component: () => import("@/components/Server/index"),
        meta: {
          permission: "dvr_servers:read",
        },
      },
      {
        path: "/servers/:page",
        name: "servers",
        component: () => import("@/components/Servers"),
        meta: {
          permission: "dvr_servers:read",
        },
      },
      {
        path: "/priority/:page",
        name: "priority",
        component: () => import("@/components/Priorities"),
        meta: {
          permission: "cameras_priorities_transactions:read",
        },
      },
      {
        path: "/users/:page",
        name: "users",
        component: () => import("@/components/Users"),
        meta: {
          permission: "employees:read",
        },
      },
      {
        path: "/user/create",
        name: "user:create",
        component: () => import("@/components/User/components/UserCreate"),
        meta: {
          permission: "employees:create",
        },
      },
      {
        path: "/user/edit/:id",
        name: "user:edit",
        props: throwId,
        component: () => import("@/components/User/components/UserEdit"),
        meta: {
          permission: "employees:read",
        },
      },
      {
        path: "/clients/mass-create",
        name: "clients:mass-create",
        component: () => import("@/components/Clients/ClientsMassCreate"),
        props: throwId,
        meta: {
          permission: "clients:create",
        },
      },
      {
        path: "/clients/:page",
        name: "clients",
        component: () => import("@/components/Clients"),
        meta: {
          permission: "clients:read",
        },
      },
      {
        path: "/client/create",
        name: "client:create",
        component: () => import("@/components/Client/components/ClientCreate"),
        meta: {
          permission: "clients:create",
        },
      },
      {
        path: "/client/edit/:id",
        name: "client:edit",
        component: () => import("@/components/Client/components/Client"),
        props: throwId,
        meta: {
          permission: "clients:read",
        },
      },
      {
        path: "/intercoms/:page",
        name: "intercoms",
        component: () => import("@/components/Intercoms"),
        meta: {
          permission: "intercoms:read",
        },
      },
      {
        path: "/intercom/create",
        name: "intercom:create",
        component: () => import("@/components/Intercom/components/IntercomCreate"),
        meta: {
          permission: "intercoms:create",
        },
      },
      {
        path: "/intercom/edit/:id",
        name: "intercom:edit",
        component: () => import("@/components/Intercom/components/Intercom"),
        props: throwId,
        meta: {
          permission: "intercoms:read",
        },
      },
      {
        path: "/tariffs",
        name: "tariffs",
        component: () => import("@/components/Tariffs/components/TariffsList"),
        meta: {
          permission: "tariffs:read",
        },
      },
      {
        path: "/tariffs/create",
        name: "tariff:create",
        component: () => import("@/components/Tariffs/components/TariffCreate"),
        meta: {
          permission: "tariffs:create",
        },
      },
      {
        path: "/billing",
        name: "billing",
        component: () => import("@/components/Billing/components/Billing"),
        meta: {
          permission: "billing:read",
        },
      },
      {
        path: "/orgs/:page",
        name: "orgs",
        component: () => import("@/components/Orgs"),
        meta: {
          permission: "orgs:read",
        },
      },
      {
        path: "/org/create",
        name: "org:create",
        component: () => import("@/components/Org/components/OrgCreate"),
        meta: {
          permission: "orgs:create",
        },
      },
      {
        path: "/org/edit/:id",
        name: "org:edit",
        component: () => import("@/components/Org/components/Org"),
        props: throwId,
        meta: {
          permission: "orgs:read",
        },
      },
      {
        path: "/sip-servers/:page",
        name: "sip-servers",
        component: () => import("@/components/SipServers"),
        meta: {
          permission: "sip_servers:read",
        },
      },
      {
        path: "/sip-server/create",
        name: "sip-server:create",
        component: () => import("@/components/SipServer/components/SipServerCreate"),
        meta: {
          permission: "sip_servers:create",
        },
      },
      {
        path: "/sip-server/edit/:id",
        name: "sip-server:edit",
        component: () => import("@/components/SipServer/components/SipServer"),
        props: throwId,
        meta: {
          permission: "sip_servers:read",
        },
      },
    ],
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/components/Auth"),
    meta: {
      public: true,
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/auth",
    name: "authByToken",
    component: () => import("@/components/Auth/AuthByToken"),
    props: (route) => {
      return {
        operator: +route.query.operator || null,
        token: Vue.$cookies.get('vk-operator-token') || "",
        redirectPath: route.query.redirectPath || "",
      };
    },
    meta: {
      public: true,
      onlyWhenLoggedOut: false,
    },
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
});

router.beforeEach(async (to, from, next) => {
  const isPublic = to.matched.some((r) => r.meta.public);
  const isLoggedIn = store.getters["auth/LOGGED_IN"];
  const profile = store.state.profile.profile;
  const onlyWhenLoggedOut = isPublic && to.matched.some((r) => r.meta.onlyWhenLoggedOut);
  const permission = to.meta.permission || "";

  if (isPublic) {
    return isLoggedIn && onlyWhenLoggedOut ? next({ name: "dashboard" }) : next();
  } else {
    if (!isLoggedIn) {
      store.commit("auth/SET_TOKEN", null);
      return next({ name: "login" });
    }

    // Для авторизованного
    if (!profile) {
      await store.dispatch("profile/GET_PROFILE");
    }

    if (permission && !checkPermission(permission)) {
      return next({ name: "dashboard" });
    }

    return next();
  }
});

export default router;
